import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { ExperienceMode, UserUiPreferences } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import { resolveExperienceMode } from './resolveExperienceMode';

const QK = ['user', 'ui-preferences'] as const;

export function useExperienceMode(): {
  mode: ExperienceMode | undefined;
  setMode: (mode: ExperienceMode) => void;
  isLoading: boolean;
  error: Error | null;
} {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const queryClient = useQueryClient();

  const query = useQuery<UserUiPreferences, Error>({
    queryKey: QK,
    queryFn: () => getSDK().user.getUiPreferences(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });

  const mutation = useMutation<
    UserUiPreferences,
    Error,
    ExperienceMode,
    { prev?: UserUiPreferences }
  >({
    mutationFn: (mode) => getSDK().user.updateUiPreferences({ experience_mode: mode }),
    onMutate: async (mode) => {
      const prev = queryClient.getQueryData<UserUiPreferences>(QK);
      queryClient.setQueryData<UserUiPreferences>(QK, { experience_mode: mode });
      await queryClient.cancelQueries({ queryKey: QK });
      return { prev };
    },
    onError: (_e, _mode, ctx) => {
      if (ctx?.prev !== undefined) queryClient.setQueryData(QK, ctx.prev);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<UserUiPreferences>(QK, data);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QK });
    },
  });

  let mode: ExperienceMode | undefined;
  if (query.isSuccess) {
    mode = resolveExperienceMode(query.data);
  } else if (query.data !== undefined) {
    mode = resolveExperienceMode(query.data);
  } else if (query.isPending) {
    mode = 'normal';
  }

  return {
    mode,
    setMode: (next) => mutation.mutate(next),
    isLoading: query.isPending && query.data === undefined,
    error: mutation.error ?? query.error ?? null,
  };
}
